import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react'
import { DELETE_BIIL_PAYMENT, GET_BILLS } from '../../services/gastosFijos/gastosFijosServices';
import { Button, Card, DatePicker, DateRangePicker, DateRangePickerItem, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Title } from '@tremor/react';
import moment from 'moment/moment';
import { RiDeleteBin2Fill, RiMoneyDollarBoxLine, RiRefreshLine } from '@remixicon/react';
import { es } from 'date-fns/locale';
import ConfirmationModal from '../shared/ConfirmationModal';
import Notification from '../shared/NotificationAlert';
import { GET_CALCULATIONS_BY } from '../../services/tablero/TableroService';

const HistorialGastosFijos = () => {
    const { loading, error, data, refetch } = useQuery(GET_BILLS);
    const [month, setSelectedMonth] = useState( new Date().getMonth() );
    const [deleteBillPayment, { idType }] = useMutation(DELETE_BIIL_PAYMENT, {
        refetchQueries: [
            { 
                query: GET_BILLS,
            },
            {
                query: GET_CALCULATIONS_BY,
                variables: { month },
            }
        ],
        onCompleted: () => {
            handleShowNotification(`El gasto fijo fue eliminado con éxito.`)
            updateDates();
        },
        onError: (error) => {
            console.error('Error al eliminar el gasto fijo:', error.message);
        }
    });

    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const today = moment().startOf('day').toDate();
    const todayEnd = moment().endOf('day').toDate();

    // Define el estado inicial con la fecha de hoy
    const [value, setValue] = useState({
        from: today ,
        to: todayEnd 
      });

    const [message, setMessage] = useState('');
    const [showNotification, setShowNotification] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [title, setTitle] = useState('');

    const handleOpenConfirmModal = (title, message, id) => {
        setMessage(message)
        setTitle(title)
        setSelectedId(id);
        setConfirmModalOpen(true)
    };
    const handleCloseConfirmModal = () => {
        setConfirmModalOpen(false);
      };
    
    const handleConfirmAction = (id) => {
        deleteBillPaymentById(id)
        handleCloseConfirmModal();
    };

    const handleShowNotification = (message) => {
        setMessage(message)
        setShowNotification(true);
        setTimeout(() => {
          setShowNotification(false);
        }, 3000);
    };

    const deleteBillPaymentById = (paymentId) => {
        deleteBillPayment({ 
            variables: { id: paymentId} 
        })
      };

      // Maneja el cambio de valor en el componente de DateRangePicker
    const handleValueChange = (newValue) => {
        // Establecer el valor 'to' como la última hora del día si se ha definido un 'to'
        if (newValue.to) {
          const endOfDay = moment(newValue.to).endOf('day').toDate();
          setValue({ from: newValue.from, to: endOfDay });
        } else {
            setValue(newValue);
        }
    };

    // Maneja la actualización de las fechas
    const updateDates = () => {
        if (value.from && value.to) {
          refetch({
              startDate: moment(value.from).unix(),
              endDate: moment(value.to).unix()
          });
        }
      };
  
  return (
    <Card>
        <ConfirmationModal
            isOpen={confirmModalOpen}
            onClose={handleCloseConfirmModal}
            message={message}
            onConfirm={handleConfirmAction}
            onCancel={handleCloseConfirmModal}
            confirmText="Aceptar"
            cancelText="Cancelar"
            title={title}
            id={selectedId}
        />
        {showNotification && <Notification message={message} />}
        <div className="flex flex-col md:flex-row gap-5 md:gap-0 md:justify-between">
            <Title className="flex-1">Registros de gastos fijos</Title>
            <div className="flex items-center space-x-2">
                <DateRangePicker
                    className="mx-auto max-w-md"
                    value={value}
                    onValueChange={handleValueChange}
                    locale={es} // Establecer el locale español
                    selectPlaceholder="Seleccionar"
                    placeholder="Selecciona un rango de fecha"
                    maxDate={new Date()}
                    enableClear={false}
                    color="rose"
                >
                <DateRangePickerItem
                    key="today"
                    value="today"
                    from={moment().startOf('day').toDate()} // Comenzar desde el principio del día actual
                    to={moment().endOf('day').toDate()} // Terminar al final del día actual
                >
                    Hoy
                </DateRangePickerItem>
                <DateRangePickerItem
                    key="last7days"
                    value="last7days"
                    from={moment().subtract(7, 'days').startOf('day').toDate()} // Hace 7 días desde el principio del día actual
                    to={moment().endOf('day').toDate()} // Terminar al final del día actual
                >
                    Últimos 7 días
                </DateRangePickerItem>
                <DateRangePickerItem
                    key="last30days"
                    value="last30days"
                    from={moment().subtract(30, 'days').startOf('day').toDate()} // Hace 30 días desde el principio del día actual
                    to={moment().endOf('day').toDate()} // Terminar al final del día actual
                >
                    Últimos 30 días
                </DateRangePickerItem>
                <DateRangePickerItem
                    key="monthToDate"
                    value="monthToDate"
                    from={moment().startOf('month').toDate()} // Comenzar desde el principio del mes actual
                    to={moment().endOf('day').toDate()} // Terminar al final del día actual
                >
                    Del mes a la fecha
                </DateRangePickerItem>
                <DateRangePickerItem
                    key="yearToDate"
                    value="yearToDate"
                    from={moment().startOf('year').toDate()} // Comenzar desde el principio del año actual
                    to={moment().endOf('day').toDate()} // Terminar al final del día actual
                >
                    Del año a la fecha
                </DateRangePickerItem>
                </DateRangePicker>
                <Button icon={RiRefreshLine} onClick={updateDates}></Button>
            </div>

        </div>
        <Table>
            <TableHead>
                <TableRow>
                    <TableHeaderCell>Nombre</TableHeaderCell>
                    <TableHeaderCell>Tipo</TableHeaderCell>
                    <TableHeaderCell>Monto</TableHeaderCell>
                    <TableHeaderCell>Fecha</TableHeaderCell>
                    <TableHeaderCell>Eliminar</TableHeaderCell>
                </TableRow>
            </TableHead>
            <TableBody>
            {data?.getBillsPayments?.length === 0 ? (
                <TableRow>
                    <TableCell colSpan={6} className="text-center">
                        No se encontraron registros de pagos fijos.
                    </TableCell>
                </TableRow>
            ) : (
                data?.getBillsPayments.map((bill) => (
                <TableRow key={bill.nombre}>
                    <TableCell>{bill.nombre}</TableCell>
                    <TableCell>{bill.tipo}</TableCell>
                    <TableCell>
                        ${bill.monto.toLocaleString('en-US')}
                    </TableCell>
                    <TableCell>
                        <span>
                            {moment.unix(bill.fecha_pago).format('DD/MM/YYYY')}
                        </span>
                    </TableCell>
                    <TableCell className="w-20">
                        <Button
                        className="bg-red-600 border-transparent hover:border-transparent hover:bg-red-700"
                        icon={RiDeleteBin2Fill}
                        onClick={() =>
                            handleOpenConfirmModal(
                            '¿Deseas eliminar este gasto fijo?',
                            'El gasto fijo se eliminará permanentemente.',
                            bill._id
                            )
                        }
                        />
                    </TableCell>
                </TableRow>
                )))}
            </TableBody>
        </Table>
    </Card>
  )
}

export default HistorialGastosFijos
